import { useState } from "react";
import { useDispatch } from "react-redux";
import Papa from "papaparse";
import ErrorText from "./Typography/ErrorText";
import { showNotification } from "../features/common/headerSlice";
import api from "../utils/axios";

const ERROR_FILE_REQUIRED = "file_required";
const ERROR_INVALID_HEADER = "header_has_invalid_columns";

/**
 * Shared CSV import modal body.
 *
 * Props:
 *  - closeModal: function to close the modal
 *  - expectedHeaders: string[] of required CSV column names
 *  - apiEndpoint: base URL for the bulk-create POST (e.g. "api/messages/all/")
 *  - buildRow: (csvRow) => object to send to API
 *  - onSuccess: () => void  called after all batches succeed
 *  - entityName: display label used in notifications (e.g. "Messages")
 */
function ImportFromCSVModalBody({
  closeModal,
  expectedHeaders,
  headerDescriptions,
  apiEndpoint,
  buildRow,
  onSuccess,
  entityName = "Messages",
}) {
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageType, setErrorMessageType] = useState("");
  const [file, setFile] = useState();

  const fileReader = new FileReader();

  const readFile = (replace = false) => {
    if (!file) {
      setErrorMessage("File is required!");
      setErrorMessageType(ERROR_FILE_REQUIRED);
      return;
    }
    fileReader.onload = async function (event) {
      const csvOutput = event.target.result;

      // Parse CSV using PapaParse - handles BOM, quoted fields, line endings, etc.
      const parseResult = Papa.parse(csvOutput, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.replace(/^\uFEFF/, "").trim(),
      });

      if (parseResult.errors.length > 0) {
        console.warn("CSV parsing warnings:", parseResult.errors);
      }

      // Validate header row
      const parsedHeaders = (parseResult.meta?.fields || []).map((h) => (h || "").trim());
      const headersMatch =
        parsedHeaders.length === expectedHeaders.length &&
        expectedHeaders.every((expected, index) => parsedHeaders[index] === expected);

      if (!headersMatch) {
        setErrorMessage("Wrong CSV Format!");
        setErrorMessageType(ERROR_INVALID_HEADER);
        return;
      }

      closeModal();
      let messageImportList = [];

      const rows = parseResult.data || [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] || {};

        // Skip rows where all relevant values are empty
        const isEmpty = expectedHeaders.every((k) => !row[k] || row[k].toString().trim() === "");
        if (isEmpty) continue;

        messageImportList.push(buildRow(row));
      }

      // batching because request payload size
      try {
        for (let i = 0; i < messageImportList.length; i += 25) {
          const messageSlice = messageImportList.slice(i, i + 25);
          const replaceAll = i === 0 ? replace : false;
          await api.post(apiEndpoint + replaceAll, messageSlice);
        }

        dispatch(
          showNotification({
            message: messageImportList.length + ` ${entityName} Imported Successfully!`,
            status: 1,
          }),
        );
        // Add a small delay to ensure server has processed all batches
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 500);
        }
      } catch (error) {
        console.error(`Error importing ${entityName.toLowerCase()}:`, error);
        dispatch(
          showNotification({
            message: `Failed to import ${entityName.toLowerCase()}. Please try again.`,
            status: 0,
          }),
        );
      }
    };

    fileReader.readAsText(file);
  };

  const updateFormValue = (e) => {
    setErrorMessage("");
    setErrorMessageType("");
    setFile(e.target.files[0]);
  };

  return (
    <>
      <div className="bg-base-200 rounded-lg p-4 mb-4 text-sm">
        <p className="font-semibold mb-1">Required CSV Format</p>
        <p className="mb-1">
          Header row:{" "}
          <code className="bg-base-300 px-1 rounded text-xs">{expectedHeaders.join(",")}</code>
        </p>
        {headerDescriptions && headerDescriptions.length > 0 && (
          <table className="table table-xs w-full mt-1">
            <thead>
              <tr>
                <th>Column</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {headerDescriptions.map((h) => (
                <tr key={h.name}>
                  <td className="font-mono">{h.name}</td>
                  <td>{h.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <input type="file" accept=".csv" onChange={updateFormValue} />

      {errorMessageType === ERROR_INVALID_HEADER ? (
        <ErrorText styleClass="mt-5">
          Please follow correct CSV format!
          <br />
          (First row must be: {expectedHeaders.join(', ')})
        </ErrorText>
      ) : (
        <ErrorText styleClass="mt-5">{errorMessage}</ErrorText>
      )}

      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button
          className="btn btn-primary px-6"
          onClick={() => readFile(false)}
        >
          Import
        </button>
        <button className="btn btn-error px-6" onClick={() => readFile(true)}>
          Import and Replace all
        </button>
      </div>
    </>
  );
}

export default ImportFromCSVModalBody;
